import { Button, MultiSelect, Stack } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../components/SimpleBreadcrumbs";
import { FormattedMessage, useIntl } from "react-intl";
import { UserCellRenderer } from "../../utils/columns";
import { useTRPC } from "../../trpc";
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "../../components/Loader";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridTable } from "../../components/AgGridTable.js";

export const Chats = () => {
  const intl = useIntl();
  const trpc = useTRPC();

  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const { data: accounts = [] } = useQuery(trpc.getAccounts.queryOptions());
  const { data: users = [] } = useQuery(trpc.getUsersForAccounts.queryOptions({
    accountIds: selectedAccounts.map(Number),
  }));
  const { data: chats = [], isLoading } = useQuery(trpc.getAccountsChats.queryOptions({
    accountIds: selectedAccounts.map(Number),
    userIds: selectedUsers.map(Number),
  }));

  const chatsWithAccounts = useMemo(() => chats.map(c => ({
    ...c,
    accountName: accounts.find(a => a.id === c.accountId)?.name,
  })), [accounts, chats]);

  type Chat = typeof chatsWithAccounts[number];

  const handleInspect = useCallback((chatId: number, accountName?: string) => () => {
    if (!accountName) return;
    window.open(`/admin/inspect/${accountName}/chats/${chatId}/ai`, "_blank");
  }, []);

  const InspectButtonRenderer = ({ data }: ICellRendererParams<Chat>) => {
    if (!data) return null;
    const { id, accountName } = data;
    return (
      <Button
        variant="primary"
        onClick={handleInspect(id, accountName)}
        size="xs"
      >
        <FormattedMessage defaultMessage="Inspect" id="button.inspect" />
      </Button>
    );
  };

  // @ts-expect-error TODO: fix type
  const columnDefs: ColDef<Chat>[] = useMemo(() => [
    {
      field: "messageCount",
      headerName: intl.formatMessage({ defaultMessage: "Message count", id: "column.messageCount" }),
      width: 150,
    },
    {
      field: "summary",
      headerName: intl.formatMessage({ defaultMessage: "Summary", id: "column.summary" }),
      width: 320,
    },
    {
      field: "user",
      headerName: intl.formatMessage({ defaultMessage: "User", id: "column.user" }),
      cellRenderer: UserCellRenderer,
    },
    {
      field: "accountName",
      headerName: intl.formatMessage({ defaultMessage: "Account", id: "column.accountName" }),
    },
    {
      field: "id",
      headerName: intl.formatMessage({ defaultMessage: "Chat id", id: "column.chatId" }),
    },

    {
      field: "createdAt",
      headerName: intl.formatMessage({ defaultMessage: "Created at", id: "column.createdAt" }),
      sortable: true,
      cellDataType: "dateTimeString",
    },
    {
      headerName: intl.formatMessage({ defaultMessage: "Actions", id: "column.actions" }),
      cellRenderer: InspectButtonRenderer,
      sortable: false,
      filter: false,
      resizable: false,
      width: 120,
    },
  ], [intl, InspectButtonRenderer]);

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Admin", id: "navigation.admin" })}
        current={intl.formatMessage({ defaultMessage: "Chats", id: "navigation.chats" })}
        description={intl.formatMessage({ defaultMessage: "See and manage chats", id: "text.chatsDescription" })}
      />
      <Stack gap="xs">
        <MultiSelect
          onChange={setSelectedAccounts}
          value={selectedAccounts}
          label={intl.formatMessage({ defaultMessage: "Select accounts", id: "label.selectAccounts" })}
          data={accounts.map(({ id, name }) => ({ value: id.toString(), label: name }))}
          limit={20}
          searchable
        />
        <MultiSelect
          onChange={setSelectedUsers}
          value={selectedUsers}
          label={intl.formatMessage({ defaultMessage: "Select users (optional)", id: "label.selectUsers" })}
          data={users.map(({ id, name }) => ({ value: id.toString(), label: name }))}
          limit={20}
          searchable
        />
      </Stack>

      {isLoading && <Loader />}
      {chatsWithAccounts.length > 0 && (
        <AgGridTable
          rowData={chatsWithAccounts}
          columnDefs={columnDefs}
          domLayout="autoHeight"

          autoSizeStrategy={{
            type: "fitGridWidth",
            defaultMinWidth: 100,
          }}
          suppressHorizontalScroll={false}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
          paginationPageSize={20}
          pagination={true}
        />
      )}
    </Stack>
  );
};
